import {
    certValidationSchemas,
    EventFormat,
    formatCertType,
    generateFullName,
    RankPrintType,
    validationOptions,
} from "@bujus/common";
import { useApiServiceContext } from "@bujus/common-frontend";
import { joiResolver } from "@hookform/resolvers/joi";
import { Print as PrintIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useForm as useFormController } from "react-hook-form";

import { DialogClosingButton, Form } from "@/base";
import { useSelectedEventStateContext } from "@/contexts";
import { useSnackbarsController } from "@/hooks";

import { RankPrintTypeSelect } from "../../common";

const CertPrintingDialog = ({ cert, close, isOpen }) => {
    const snackbarsController = useSnackbarsController();

    const apiService = useApiServiceContext();
    const [selectedEvent] = useSelectedEventStateContext();

    const formController = useFormController({
        mode: "onChange",
        resolver: joiResolver(certValidationSchemas.print.form, validationOptions),
        defaultValues: {
            rankPrintType: selectedEvent.format === EventFormat.CONTEST ? RankPrintType.FAKE_POINTS : RankPrintType.RANK,
        },
    });

    const mutation = useMutation(
        async (variables) =>
            apiService.certs.printAsync(variables.id, variables.rankPrintType),
        {
            onError: (error) => {
                snackbarsController.enqueueError(error);
            },
            onSuccess: (data) => {
                snackbarsController.enqueueSuccessMessage("Urkunde gedruckt");
                snackbarsController.enqueuePopUpWarning();
                close();
                window.open(data, "_blank");
            },
        },
    );

    const tryClose = () => {
        if (mutation.isLoading) {
            return false;
        }
        close();
        return true;
    };

    const handleClose = () => {
        tryClose();
    };

    const handleClosingButtonClick = () => {
        tryClose();
    };

    const handleFormSubmit = (formData) => {
        mutation.mutate({
            id: cert.id,
            rankPrintType: formData.rankPrintType,
        });
    };

    return (
        <Dialog onClose={handleClose} open={isOpen}>
            <Form controller={formController} onSubmit={handleFormSubmit}>
                <DialogTitle>Urkunde drucken</DialogTitle>
                <DialogClosingButton
                    isDisabled={mutation.isLoading}
                    onClick={handleClosingButtonClick}
                />
                <DialogContent>
                    <DialogContentText>
                        <Typography variant="dialogContentTextLong">
                            <b>Teilnehmer Name:</b>{" "}
                            {generateFullName(
                                cert.participant.name.first,
                                cert.participant.name.last,
                            )}
                        </Typography>
                        {cert.participant.schoolInternalId !== undefined && (
                            <Typography variant="dialogContentTextLong">
                                <b>Teilnehmer Schulinterne-ID:</b>{" "}
                                {cert.participant.schoolInternalId}
                            </Typography>
                        )}
                        <Typography variant="dialogContentTextLong">
                            {selectedEvent.format === EventFormat.CONTEST ? (
                                <>
                                    <b>Rang: </b> {cert.points} von {cert.groupParticipantCount}
                                </>
                            ) : (
                                <>
                                    <b>Punkte: </b> {cert.points}
                                </>
                            )}
                        </Typography>
                        <Typography variant="dialogContentTextLong">
                            <b>Typ:</b> {formatCertType(cert.type)}
                        </Typography>
                    </DialogContentText>
                    {selectedEvent.format === EventFormat.CONTEST && (
                        <Box mt={2}>
                            <RankPrintTypeSelect
                                fieldName="rankPrintType"
                                isEnabled={!mutation.isLoading}
                                label="Was steht auf der Urkunde?"
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        loading={mutation.isLoading}
                        startIcon={<PrintIcon />}
                        type="submit"
                    >
                        Drucken
                    </LoadingButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
};

export { CertPrintingDialog };
